import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { useFilamentText } from '../../../hooks/useFilamentText';
import { useSEO } from '../../../hooks/useSEO';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const PETGPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const TEXT = useFilamentText();

  useSEO({
    title: 'PETG',
    description: isEnglish 
      ? 'Stronger than PLA, easier than ABS. High transparency, excellent chemical resistance, food-safe. Detailed explanation of recommended settings and features.'
      : 'PLAより高強度、ABSより扱いやすい。透明度が高く、化学的耐性に優れ、食品安全基準適合。推奨設定、特徴を詳しく解説。',
    keywords: '3Dプリンター,PETG,フィラメント,透明,食品安全,化学耐性',
    canonicalPath: isEnglish ? '/en/filament/petg' : '/filament/petg'
  });

  const breadcrumbItems = isEnglish ? [
    { label: 'Home', path: '/en' },
    { label: 'Filament Data', path: '/filament' },
    { label: 'PETG', path: '/en/filament/petg' }
  ] : [
    { label: 'ホーム', path: '/' },
    { label: 'フィラメントデータ', path: '/filament' },
    { label: 'PETG', path: '/filament/petg' }
  ];

  const filamentId = 'petg';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            {/* タイトル */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                PETG
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="petg" />

            {/* 特徴（最上部に大きく配置） */}
            <div className="metal-panel p-8 border-l-4 border-accent">
              <h2 className="text-3xl font-semibold text-white font-['Exo'] mb-6 accent-line pb-3">
                {TEXT.features}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ul className="text-metal-light space-y-3 text-lg">
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Stronger than PLA, easier than ABS' : 'PLAより高強度、ABSより扱いやすい'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'High transparency' : '透明度が高い'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Excellent chemical resistance' : '化学的耐性に優れる'}</span>
                  </li>
                </ul>
                <ul className="text-metal-light space-y-3 text-lg">
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Food-safe certified' : '食品安全基準適合'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Recyclable' : 'リサイクル可能'}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <i className="ri-check-line text-green-400 mt-1"></i>
                    <span>{isEnglish ? 'Excellent impact resistance' : '優れた耐衝撃性'}</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.classification}</span>
                      <span className="difficulty-intermediate px-3 py-1 rounded text-sm font-semibold">{TEXT.intermediate}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.baseResin}</span>
                      <span className="text-white">{isEnglish ? 'Polyethylene Terephthalate Glycol' : 'ポリエチレンテレフタレートグリコール'}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.meltingPoint}</span>
                      <span className="text-white">245-250°C</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.density}</span>
                      <span className="text-white">1.27 g/cm³</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.shrinkageRate}</span>
                      <span className="text-white">0.2-0.4%</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{TEXT.heatResistance}</span>
                      <span className="text-white">70-80°C</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{TEXT.item}</th>
                      <th className="px-4 py-3 text-left">{TEXT.recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{TEXT.notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">220-250°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Lower temp for transparency' : '透明度重視なら低温側'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.bedTemp}</td>
                      <td className="px-4 py-3 text-white">70-80°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'For better adhesion' : '密着性向上のため'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.fanSetting}</td>
                      <td className="px-4 py-3 text-white">30-50%</td>
                      <td className="px-4 py-3">{isEnglish ? 'Too strong causes poor layer adhesion' : '強すぎると層間接着不良'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.printSpeed}</td>
                      <td className="px-4 py-3 text-white">30-50mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? 'Slow speed prevents stringing' : '糸引き防止のため低速'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{TEXT.nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">0.4-0.6mm</td>
                      <td className="px-4 py-3">{isEnglish ? '0.6mm reduces stringing' : '0.6mmで糸引き軽減'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{TEXT.caution}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Prone to stringing, retraction adjustment required' : '糸引きしやすい、リトラクション調整必須'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 応力-ひずみグラフ（CSV自動探索） */}
            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="PETG"
            />

            {/* 派生・バリエーション */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.derivatives}
              </h2>
              <p className="text-metal-light mb-6">
                {isEnglish 
                  ? 'Various derivative filaments with enhanced properties have been developed based on PETG. Choose the optimal one according to your application.'
                  : 'PETGをベースに、様々な特性を強化した派生フィラメントが開発されています。用途に応じて最適なものを選択してください。'}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <Link 
                  to={isEnglish ? "/en/filament/petg" : "/filament/petg"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">PETG</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Standard PETG. Well-balanced strength, transparency, and chemical resistance.' : '標準PETG。高強度・透明性・化学耐性のバランスが良い。'}</p>
                </Link>
                <Link 
                  to={isEnglish ? "/en/filament/petg-cf" : "/filament/petg-cf"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">PETG-CF</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Carbon fiber reinforced. Significantly improved rigidity, hardened nozzle recommended.' : 'カーボン繊維強化版。剛性大幅向上、硬化ノズル推奨。'}</p>
                </Link>
                <Link 
                  to={isEnglish ? "/en/filament/petg-gf" : "/filament/petg-gf"}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">PETG-GF</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Glass fiber reinforced. Improved heat resistance and strength, for industrial parts.' : 'ガラス繊維強化。耐熱性・強度向上、工業部品向け。'}</p>
                </Link>
              </div>
            </div>

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '向いている造形'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    <li>• {isEnglish ? 'Mechanical parts & jigs' : '機械部品・治具'}</li>
                    <li>• {isEnglish ? 'Transparent containers & cases' : '透明容器・ケース'}</li>
                    <li>• {isEnglish ? 'Food contact applications' : '食品接触用途'}</li>
                    <li>• {isEnglish ? 'Durable products' : '耐久性重視の製品'}</li>
                    <li>• {isEnglish ? 'Medical device parts' : '医療機器部品'}</li>
                    <li>• {isEnglish ? 'Chemical containers' : '化学薬品容器'}</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : '長所・短所'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : '長所：'}</span>
                      <span className="text-metal-light"> {isEnglish ? 'High strength, transparent, chemical resistant, food-safe' : '高強度、透明、化学耐性、食品安全'}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : '短所：'}</span>
                      <span className="text-metal-light"> {isEnglish ? 'Stringing, requires tuning, expensive' : '糸引き、設定調整必要、高価'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {TEXT.precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                <p>• {isEnglish ? 'Prone to stringing, optimizing retraction settings is important.' : '糸引きが発生しやすいため、リトラクション設定の最適化が重要です。'}</p>
                <p>• {isEnglish ? 'Bed adhesion may be too strong, be careful when removing prints.' : 'ベッドへの密着が強すぎる場合があり、剥離時に注意が必要です。'}</p>
                <p>• {isEnglish ? 'High-temperature printing requires adequate ventilation.' : '高温での印刷のため、換気を十分に行ってください。'}</p>
                <p>• {isEnglish ? 'For transparency, set printing temperature lower.' : '透明度を重視する場合は、印刷温度を低めに設定してください。'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PETGPage;
